import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { toast } from '@/hooks/use-toast'
import { useCreateOrganization } from '@/features/admin/organizationsQueries'
import type { ApiRequestError } from '@/services/api'

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  tenant_id: z.string().uuid('Must be a valid UUID'),
  type: z.enum(['standard', 'partner']),
})

type FormValues = z.infer<typeof schema>

interface CreateOrganizationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  canCreatePartner: boolean
}

export function CreateOrganizationDialog({
  open,
  onOpenChange,
  canCreatePartner,
}: CreateOrganizationDialogProps) {
  const createOrganization = useCreateOrganization()
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', tenant_id: '', type: 'standard' },
  })

  function close() {
    reset()
    onOpenChange(false)
  }

  const onSubmit = handleSubmit(async (values) => {
    try {
      await createOrganization.mutateAsync({
        name: values.name,
        tenant_id: values.tenant_id,
        type: canCreatePartner ? values.type : undefined,
      })
      toast({ title: 'Organization created' })
      close()
    } catch (error) {
      toast({
        variant: 'destructive',
        description: (error as ApiRequestError).message ?? 'Could not create organization',
      })
    }
  })

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create organization</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="org-name">
              Name
            </label>
            <Input id="org-name" {...register('name')} />
            {errors.name && <p className="text-xs text-red-600">{errors.name.message}</p>}
          </div>
          <div className="space-y-1">
            <label className="text-sm font-medium text-gray-700" htmlFor="org-tenant-id">
              Tenant ID
            </label>
            <Input id="org-tenant-id" placeholder="00000000-0000-0000-0000-000000000000" {...register('tenant_id')} />
            {errors.tenant_id && <p className="text-xs text-red-600">{errors.tenant_id.message}</p>}
          </div>
          {canCreatePartner && (
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Type</label>
              <Select value={watch('type')} onValueChange={(v) => setValue('type', v as FormValues['type'])}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="partner">Partner</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <Button type="submit" disabled={createOrganization.isPending}>
              {createOrganization.isPending ? 'Creating…' : 'Create organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
